import React, { useState, useEffect } from 'react';
import client from '@js/modules/httpClient/index';

type FacebookLoginResponse = {
  status?: string;
  authResponse?: Record<string, unknown>;
};

type FacebookUserInfo = Record<string, unknown> & {
  status?: string;
  email?: string;
  error?: { type: string; message: string };
};

declare global {
  interface Window {
    FB?: {
      init: (options: Record<string, unknown>) => void;
      login: (
        callback: (response: FacebookLoginResponse) => void,
        options: Record<string, unknown>
      ) => void;
      api: (
        path: string,
        options: Record<string, unknown>,
        callback: (response: FacebookUserInfo) => void
      ) => void;
    };
    fbAsyncInit?: () => void;
  }
}

export function SnsAuth() {
  const [errorText, setErrorText] = useState('');
  const [appId, setAppId] = useState('');
  const [isSdkLoaded, setIsSdkLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const stateCode = 'fincf-facebook-app-id';

    client.get(`/api/v1/customer/facebook_auth/facebook_app_id?state=${stateCode}`)
      .then((response) => {
        setAppId(response.data.id);
      })
      .catch(() => {
        setAppId('');
      });
  }, []);

  useEffect(() => {
    if(appId == '') return;

    let isMounted = true;

    const initializeFacebookSdk = () => {
      window.FB?.init({
        appId,
        cookie: false,
        xfbml: false,
        version: 'v2.3',
      });

      if(isMounted) setIsSdkLoaded(true);
    };

    window.fbAsyncInit = initializeFacebookSdk;

    if(window.FB) {
      initializeFacebookSdk();
      return () => {
        isMounted = false;
      };
    }

    const fbRootId = 'fb-root';
    if(!document.getElementById(fbRootId)) {
      const fbRoot = document.createElement('div');
      fbRoot.id = fbRootId;
      document.body.appendChild(fbRoot);
    }

    const sdkId = 'facebook-jssdk';
    if(!document.getElementById(sdkId)) {
      const script = document.createElement('script');
      const firstScript = document.getElementsByTagName('script')[0];
      script.id = sdkId;
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      firstScript.parentNode?.insertBefore(script, firstScript);
    }

    return () => {
      isMounted = false;
    };
  }, [appId]);

  function sendUserInfo(userInfo: Record<string, unknown>) {
    client.post('/api/v1/customer/facebook_auth', userInfo)
      .then((response) => {
        const path = response['data']['path'];
        window.location.href = `${path}`;
      })
      .catch((error) => {
        if(
          error.error?.type === 'CustomerIsExists' ||
          error.error?.type === 'ActiveRecord::RecordInvalid'
        ) {
          setErrorText(error.error.message);
        } else {
          console.log(`FinCF API Server Error: ${error}`);
        }
      });
  }

  function responseFacebook(response: FacebookUserInfo) {
    if(response.status != 'unknown' ) {
      if(response['email'] == undefined) {
        setErrorText('メールアドレスが登録されていないため、SNS登録ができません。');
      } else {
        sendUserInfo(response);
      }
    }
  }

  function loginWithFacebook() {
    if(!window.FB || isProcessing || !isSdkLoaded) return;

    setIsProcessing(true);

    window.FB.login((loginResponse) => {
      if(!loginResponse.authResponse) {
        setIsProcessing(false);
        responseFacebook({ status: loginResponse.status });
        return;
      }

      window.FB?.api('/me', { locale: 'en_US', fields: 'email' }, (userInfo) => {
        setIsProcessing(false);
        responseFacebook({ ...userInfo, ...loginResponse.authResponse });
      });
    }, {
      scope: 'public_profile, email',
      return_scopes: false,
    });
  }

  if(appId != '') {
    return (
      <>
        <button
          type="button"
          className="button__sns--facebook metro"
          disabled={!isSdkLoaded || isProcessing}
          onClick={loginWithFacebook}
        >
          <FacebookIcon />
        </button>
        <div className='form-input__error'>{errorText}</div>
      </>
    );
  } else {
    return <></>;
  }
}

function FacebookIcon() {
  return <span className="icon__sns--facebook">facebookでログイン</span>;
}
